import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { trackServiceExpand, trackCtaClick } from '../utils/analytics';

const offerings = [
  {
    title: 'Weddings',
    tagline: 'From intimate ceremonies to destination weddings, designed end to end.',
    image: '/portfolio/drive_wedding_4.png',
    alt: 'Luxury wedding by CinCin Events',
    details: [
      'Destination weddings (India & international)',
      'Hometown & venue-based celebrations',
      'Full wedding design & decor execution',
      'Single-day & multi-day celebrations',
    ],
  },
  {
    title: 'Corporate Events & Exhibitions',
    tagline: 'Experiences that shape perception, not just presence.',
    image: '/portfolio/drive_corporate_6.png',
    alt: 'Corporate event by CinCin Events',
    imageStyle: { objectPosition: 'center 25%' },
    details: [
      'Brand & product launches',
      'Real estate & project showcases',
      'Large-format exhibitions & trade events',
      'Cultural & political exhibitions',
    ],
  },
  {
    title: 'Private Celebrations',
    tagline: 'Personal celebrations, designed with the same precision as large-scale events.',
    image: '/portfolio/drive_private_new_babyshower.jpg',
    alt: 'Private celebration by CinCin Events',
    details: [
      'Milestone birthdays',
      'Anniversaries',
      'Baby showers & intimate gatherings',
      'Hosted private experiences',
    ],
  },
];

function OfferingCard({ offering, index }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay: index * 0.1 }}
      data-testid={`offering-card-${index}`}
      className="group"
    >
      <div
        className="rounded-2xl overflow-hidden transition-all duration-500 md:hover:-translate-y-1"
        style={{
          background: 'linear-gradient(165deg, rgba(255,255,255,0.65) 0%, rgba(239,234,230,0.45) 100%)',
          boxShadow: '0 6px 24px rgba(10,10,10,0.04), 0 1px 4px rgba(10,10,10,0.02), inset 0 1px 0 rgba(255,255,255,0.5)',
        }}
      >
        {/* Image */}
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16/8' }}>
          <img
            src={offering.image}
            alt={offering.alt}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            style={offering.imageStyle || {}}
          />
          <div className="absolute inset-0 bg-[#0A0A0A]/8" />
        </div>

        {/* Content */}
        <div className="px-7 md:px-9 py-7 md:py-9">
          <h3 className="font-serif text-xl sm:text-2xl lg:text-3xl tracking-tight text-[#0A0A0A] leading-[1.2]">
            {offering.title}
          </h3>
          <p className="mt-3 font-light text-[13px] sm:text-sm leading-[1.8] text-[#0A0A0A]/50" style={{ letterSpacing: '0.2px' }}>
            {offering.tagline}
          </p>

          {/* Expandable details */}
          <button
            onClick={() => {
              if (!expanded) trackServiceExpand(offering.title.toLowerCase().replace(/[^a-z]/g, '_'));
              setExpanded(!expanded);
            }}
            data-testid={`offering-expand-${index}`}
            className="mt-5 flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#8C8C8C]/60 font-light hover:text-[#0A0A0A]/50 transition-colors duration-300"
          >
            {expanded ? 'Less' : 'What this includes'}
            <ChevronDown
              size={13}
              className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            />
          </button>

          <motion.div
            initial={false}
            animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <ul className="pt-5 space-y-3">
              {offering.details.map((d) => (
                <li key={d} className="flex items-start gap-3">
                  <span className="mt-[6px] w-[4px] h-[4px] rounded-full bg-[#D8B4A0]/40 shrink-0" />
                  <span className="font-light text-[12px] leading-[1.8] text-[#0A0A0A]/45" style={{ letterSpacing: '0.2px' }}>
                    {d}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

export default function WhatWeDoSection() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: "-80px" });

  const scrollToContact = () => {
    trackCtaClick('what_we_do');
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="experiences"
      data-testid="what-we-do-section"
      className="bg-[#EFEAE6] py-20 md:py-36 px-6 md:px-16 lg:px-24"
    >
      <div className="max-w-[1100px] mx-auto">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-24"
        >
          <span className="label-text" data-testid="what-we-do-label">What We Do</span>
          <div className="editorial-line mt-4 mb-8" />
          <h2 className="font-serif sm:text-4xl lg:text-5xl tracking-tight text-[#0A0A0A] leading-[1.2] sm:leading-[1.15] max-w-[700px]">
            <span className="block whitespace-nowrap text-[clamp(1.15rem,5.5vw,3rem)]">Designed around what you need.</span>
            <span className="block whitespace-nowrap text-[clamp(1.15rem,5.5vw,3rem)] text-[#0A0A0A]/60">Executed to the highest standard.</span>
          </h2>
          <p className="mt-8 md:mt-10 font-light text-[13px] sm:text-sm leading-[2] text-[#0A0A0A]/50 max-w-[500px]" style={{ letterSpacing: '0.3px' }}>
            We design, produce, and execute weddings, brand-led experiences, and private celebrations, with clarity, structure, and intent.
          </p>
        </motion.div>

        {/* 3 Offering Cards */}
        <div className="space-y-8 md:space-y-12">
          {offerings.map((o, i) => (
            <OfferingCard key={o.title} offering={o} index={i} />
          ))}
        </div>

        {/* Section CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 md:mt-24 text-center"
        >
          <p className="font-serif text-base sm:text-lg italic text-[#0A0A0A]/45">
            Planning something specific?<br />
            We'll help you shape it.
          </p>
          <button
            onClick={scrollToContact}
            data-testid="what-we-do-cta"
            className="mt-8 px-10 py-3.5 rounded-full border border-[#0A0A0A]/20 text-[#0A0A0A] text-[11px] tracking-[0.2em] uppercase font-light hover:bg-[#0A0A0A] hover:text-[#EFEAE6] transition-all duration-500"
          >
            Plan Your Event
          </button>
        </motion.div>
      </div>
    </section>
  );
}
